/**
 * SchoolNotifications - SMS & WhatsApp notification settings and log
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, MessageSquare, Phone, Send, Settings, CheckCircle2, XCircle,
  Bell, Smartphone, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

// ==================== SETTINGS ====================
const NotifSettings = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ['notif-settings', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/notification-settings`),
    enabled: !!tenantId,
  });
  const [form, setForm] = useState(null);

  React.useEffect(() => {
    if (settings && !form) {
      setForm({
        sms_enabled: settings.sms_enabled || false,
        whatsapp_enabled: settings.whatsapp_enabled || false,
        twilio_account_sid: settings.twilio_account_sid || '',
        twilio_auth_token: settings.twilio_auth_token || '',
        twilio_phone_number: settings.twilio_phone_number || '',
        whatsapp_api_url: settings.whatsapp_api_url || '',
        whatsapp_access_token: settings.whatsapp_access_token || '',
        whatsapp_phone_number_id: settings.whatsapp_phone_number_id || '',
        notify_on_payment: settings.notify_on_payment !== false,
        notify_on_lockout: settings.notify_on_lockout !== false,
        notify_on_fee_reminder: settings.notify_on_fee_reminder !== false,
        notify_on_announcement: settings.notify_on_announcement !== false,
      });
    }
  }, [settings, form]);

  const saveMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/notification-settings`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Notification settings saved'); queryClient.invalidateQueries({ queryKey: ['notif-settings'] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading || !form) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;

  const triggers = [
    { key: 'notify_on_payment', label: 'Payment Received', desc: 'Send confirmation when a fee payment is recorded' },
    { key: 'notify_on_lockout', label: 'Account Locked', desc: 'Notify parents when access is restricted due to unpaid fees' },
    { key: 'notify_on_fee_reminder', label: 'Fee Reminder', desc: 'Send when bulk fee reminders are triggered' },
    { key: 'notify_on_announcement', label: 'Announcements', desc: 'Send when new announcements are posted' },
  ];

  return (
    <div className="space-y-6" data-testid="notif-settings-panel">
      {/* Channel Toggles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={form.sms_enabled ? 'border-blue-300 bg-blue-50/50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">SMS (Twilio)</h3>
                  <p className="text-xs text-slate-500">Text messages via Twilio</p>
                </div>
              </div>
              <Switch checked={form.sms_enabled} onCheckedChange={v => setForm({ ...form, sms_enabled: v })} data-testid="sms-toggle" />
            </div>
            {form.sms_enabled && (
              <div className="space-y-3 pt-2 border-t">
                <div><Label className="text-xs">Account SID</Label>
                  <Input value={form.twilio_account_sid} onChange={e => setForm({ ...form, twilio_account_sid: e.target.value })}
                         placeholder="AC..." className="text-sm" data-testid="twilio-sid" /></div>
                <div><Label className="text-xs">Auth Token</Label>
                  <Input type="password" value={form.twilio_auth_token} onChange={e => setForm({ ...form, twilio_auth_token: e.target.value })}
                         placeholder="Enter auth token" className="text-sm" data-testid="twilio-token" /></div>
                <div><Label className="text-xs">Phone Number (From)</Label>
                  <Input value={form.twilio_phone_number} onChange={e => setForm({ ...form, twilio_phone_number: e.target.value })}
                         placeholder="+1234567890" className="text-sm" data-testid="twilio-phone" /></div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-emerald-300 bg-emerald-50/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <h3 className="font-semibold">WhatsApp (OpenWA)</h3>
                <p className="text-xs text-slate-500">Unified ecosystem channel</p>
              </div>
            </div>
            <p className="text-xs text-emerald-900 mb-3">
              WhatsApp now routes through the platform-wide OpenWA channel. Templates, broadcasts, opt-outs, and analytics live in one place.
            </p>
            <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="school-wa-redirect-btn">
              <a href="/admin/whatsapp">Open WhatsApp Admin →</a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Notification Triggers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notification Triggers</CardTitle>
          <CardDescription>Choose which events send notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {triggers.map(t => (
            <div key={t.key} className="flex items-center justify-between py-2 border-b last:border-0">
              <div>
                <p className="font-medium text-sm">{t.label}</p>
                <p className="text-xs text-slate-400">{t.desc}</p>
              </div>
              <Switch checked={form[t.key]} onCheckedChange={v => setForm({ ...form, [t.key]: v })} data-testid={`trigger-${t.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending} data-testid="save-notif-settings-btn">
        {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Save Notification Settings
      </Button>
    </div>
  );
};

// ==================== TEST MESSAGE ====================
const TestMessage = ({ tenantId }) => {
  const [showTest, setShowTest] = useState(false);
  const [testForm, setTestForm] = useState({ channel: 'sms', to_phone: '', message: 'Test notification from your school system.' });

  const testMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/notification-test`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (data) => {
      if (data.success) toast.success('Test message sent!');
      else toast.error(data.error || 'Failed');
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div data-testid="test-message-panel">
      <Button variant="outline" onClick={() => setShowTest(true)} data-testid="open-test-message-btn">
        <Send className="h-4 w-4 mr-2" /> Send Test Message
      </Button>
      <Dialog open={showTest} onOpenChange={setShowTest}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send Test Message</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Channel</Label>
              <Select value={testForm.channel} onValueChange={v => setTestForm({ ...testForm, channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input value={testForm.to_phone} onChange={e => setTestForm({ ...testForm, to_phone: e.target.value })}
                     placeholder="+263771234567" data-testid="test-phone" />
            </div>
            <div>
              <Label>Message</Label>
              <textarea className="w-full border rounded-lg p-3 text-sm min-h-[60px]"
                        value={testForm.message} onChange={e => setTestForm({ ...testForm, message: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => testMutation.mutate(testForm)} disabled={testMutation.isPending || !testForm.to_phone} data-testid="send-test-btn">
              {testMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== NOTIFICATION LOG ====================
const NotifLog = ({ tenantId }) => {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['notif-log', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/notification-log`),
    enabled: !!tenantId,
  });

  return (
    <div className="space-y-4" data-testid="notif-log-panel">
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>To</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>SMS</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Time</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No notifications sent yet</TableCell></TableRow>
            ) : logs.map(l => (
              <TableRow key={l.id}>
                <TableCell className="font-mono text-xs">{l.to_phone}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{(l.trigger || '').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{l.message}</TableCell>
                <TableCell>
                  {l.results?.sms ? (
                    l.results.sms.success ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />
                  ) : <span className="text-slate-300">-</span>}
                </TableCell>
                <TableCell>
                  {l.results?.whatsapp ? (
                    l.results.whatsapp.success ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />
                  ) : <span className="text-slate-300">-</span>}
                </TableCell>
                <TableCell className="text-xs text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ==================== FEE REMINDERS ====================
const FeeReminders = ({ tenantId }) => {
  const reminderMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/notification-send-reminders`, { method: 'POST' }),
    onSuccess: (data) => toast.success(`Sent: ${data.sent}, Failed: ${data.failed}, Skipped: ${data.skipped}`),
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="fee-reminders-panel">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-5 w-5 text-amber-500" /> Bulk Fee Reminders
        </CardTitle>
        <CardDescription>
          Send fee reminder notifications to all parents of students with outstanding balances.
          Messages are sent via all enabled channels (SMS and/or WhatsApp).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => reminderMutation.mutate()} disabled={reminderMutation.isPending} data-testid="send-reminders-btn">
          {reminderMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Send Fee Reminders
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== MAIN COMPONENT ====================
export const SchoolNotifications = ({ tenantId }) => {
  return (
    <div data-testid="school-notifications-module">
      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="settings" data-testid="notif-tab-settings"><Settings className="h-3.5 w-3.5 mr-1" /> Settings</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="notif-tab-reminders"><Bell className="h-3.5 w-3.5 mr-1" /> Fee Reminders</TabsTrigger>
          <TabsTrigger value="test" data-testid="notif-tab-test"><Send className="h-3.5 w-3.5 mr-1" /> Test</TabsTrigger>
          <TabsTrigger value="log" data-testid="notif-tab-log"><Clock className="h-3.5 w-3.5 mr-1" /> Log</TabsTrigger>
        </TabsList>
        <TabsContent value="settings"><NotifSettings tenantId={tenantId} /></TabsContent>
        <TabsContent value="reminders"><FeeReminders tenantId={tenantId} /></TabsContent>
        <TabsContent value="test"><TestMessage tenantId={tenantId} /></TabsContent>
        <TabsContent value="log"><NotifLog tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolNotifications;
